import { Nullable } from 'src/types/nullable.type';
import { getFileMetadata } from 'src/utils/pdf';
import { create } from 'zustand';
import { AssetResponse, uploadAssetToS3 } from '../helpers/file-upload';
import { ErrorInterface } from '../interfaces/error.interface';

export interface UploadStack {
  file: File;
  started: boolean;
  cancelled?: boolean;
  done: boolean;
  progress: number;
  cancellable: boolean;
  response: Nullable<AssetResponse>;
  copyText?: string;
  abortCall: () => void;
  s3Aborter?: () => void;
}

export interface StartUploadCallbacks {
  onSuccess?: (asset: AssetResponse, meta?: object) => void;
  onFail?: (err: ErrorInterface) => void;
  onCancel?: (name: string) => void;
}

interface Methods {
  startUpload: (callbacks?: StartUploadCallbacks) => void;
  addUploadStack: (files: File[]) => void;
  getOngoingUploadStack: (name: string) => UploadStack;
  getOngoingUploadStacks: () => Array<[string, UploadStack]>;
  removeUploadStack: (name: string) => void;
  removeLastCancelledFileName: () => void;
  resetStore: () => void;
}

interface Variables {
  stackMapping: Map<string, UploadStack>;
  stackLength: number;
  lastCancelledFileName: Nullable<string>;
}

interface OngoingUploadStack extends UploadStack {
  call: () => void;
}

type StoreState = Variables & Methods;

const initialState: Variables = {
  stackMapping: new Map(),
  stackLength: 0,
  lastCancelledFileName: null,
};

const createUploadStack = (file: File): UploadStack => ({
  file,
  done: false,
  progress: 0,
  response: null,
  cancellable: false,
  started: false,
  // placeholder func
  abortCall() {},
});

export const useFilesUploadStore = create<StoreState>((set, get) => ({
  ...initialState,
  addUploadStack(files) {
    const { stackMapping } = get();
    files
      .filter(({ name }) => !stackMapping.has(name))
      .forEach((file) => {
        stackMapping.set(file.name, createUploadStack(file));
      });
  },
  getOngoingUploadStacks() {
    const { stackMapping } = get();
    return Array.from(stackMapping);
  },

  getOngoingUploadStack(name: string) {
    const { stackMapping } = get();
    return stackMapping.get(name)!;
  },
  startUpload(callbacks) {
    const { stackMapping } = get();
    const notStartedCalls = Array.from(stackMapping).filter(([_, stack]) => !stack.started);

    const callableStacks = notStartedCalls.reduce<OngoingUploadStack[]>(
      (acc, [name, stack]) => [
        ...acc,
        {
          ...stack,
          call: () => {
            uploadAssetToS3(
              stack.file,
              (number) => {
                if (number > stack.progress) {
                  // S3 call calls that method too much with the same value
                  stackMapping.set(name, {
                    ...stack,
                    cancellable: true,
                    progress: number,
                  });

                  // updates the mapping in the store
                  set({ stackMapping, stackLength: stackMapping.size });
                }
              },
              (abortFn) => {
                stack.abortCall = () => {
                  abortFn();
                };
              }
            )
              .then(async (response: AssetResponse) => {
                if (callbacks?.onSuccess) {
                  let meta;
                  if (stack.file.type.endsWith('pdf')) {
                    meta = await getFileMetadata(stack.file);
                  }
                  callbacks.onSuccess(response, meta);
                }

                // request is completed, flags are changed.
                stackMapping.set(name, { ...stack, done: true, started: false });
                // updates the mapping in the store
                set({ stackMapping, stackLength: stackMapping.size });
              })
              .catch((err) => {
                if (err.code === 'RequestAbortedError') {
                  // request is cancelled, and done. flags are changed
                  stackMapping.set(name, { ...stack, cancelled: true, done: true, started: false });
                  // updates the mapping in the store
                  set({ stackMapping, stackLength: stackMapping.size });
                  set({ lastCancelledFileName: name });

                  if (callbacks?.onCancel) {
                    callbacks.onCancel(name);
                  }
                  return;
                }

                // request is failed, flags are changed
                stackMapping.set(name, { ...stack, cancelled: true, done: true, started: false });
                // updates the mapping in the store
                set({ stackMapping, stackLength: stackMapping.size });
                if (callbacks?.onFail) {
                  callbacks.onFail(err);
                }
              });
          },
        },
      ],
      []
    );

    // all requests are started
    Array.from(stackMapping).forEach(([name, stack]) => {
      stackMapping.set(name, { ...stack, started: true });
    });

    // updates the mapping in the store
    set({ stackMapping });

    callableStacks.forEach(({ call }) => call());
  },
  removeUploadStack(name) {
    const { stackMapping } = get();
    // removes the stack with the given name
    stackMapping.delete(name);
    set({ stackMapping, stackLength: stackMapping.size });
  },
  removeLastCancelledFileName() {
    set({ lastCancelledFileName: null });
  },
  resetStore() {
    set({ ...initialState });
  },
}));
